'use client';

import * as React from 'react';
import { type ReactNode } from 'react';
import { Box, Button, Container, Stack, Typography } from '@mui/material';
import { ArrowLeft as ArrowLeftIcon, LockOpen as LockOpenIcon } from '@phosphor-icons/react';
import { useNavigate } from 'react-router-dom';

import type { UserRole } from '@/types/v2/user-role';
import { paths } from '@/paths';
import { Page as NotFoundPage } from '@/pages/not-found';
import { COMMUNITY_ROLES } from '@/constants/community-roles';
import { logger } from '@/lib/default-logger';
import { useUser } from '@/hooks/v2/use-user';

export interface RoleGuardProps {
  children: ReactNode;
  requiredUserRoles?: UserRole[];
  requireAllUserRoles?: boolean;
  requiredCommunityRoles?: string[];
  requireAllCommunityRoles?: boolean;
  requiredAccountRoles?: string[];
  requireAllAccountRoles?: boolean;
  redirect?: boolean;
  displayUnauthorized?: boolean;
  show404?: boolean;
}

export function RoleGuard({
  children,
  requiredUserRoles,
  requireAllUserRoles = false,
  requiredCommunityRoles,
  requireAllCommunityRoles = false,
  requiredAccountRoles = [],
  requireAllAccountRoles = false,
  redirect = false,
  displayUnauthorized = false,
  show404 = false,
}: RoleGuardProps): JSX.Element | null {
  const navigate = useNavigate();
  const { user, error, community, account, isLoading } = useUser();
  const [isChecking, setIsChecking] = React.useState<boolean>(true);

  /**
   * Determines if the current user should be granted permission.
   *
   * The logic is:
   *   1. If a non-empty array is provided via `requiredUserRoles`, then check against user.roles.
   *      If the user has (all or any, depending on requireAllUserRoles) the required role(s),
   *      access is granted – otherwise, not.
   *
   *   2. Else if non-empty `requiredAccountRoles` is provided, then check account.roles.
   *
   *   3. Else if non-empty `requiredCommunityRoles` is provided, then check community.roles.
   *
   *   4. If none are provided, then access is granted by default.
   *
   * In this way, the user roles "supersede" the account and community roles.
   */
  const getHasAccess = () => {
    // First check user roles (highest priority)
    if (requiredUserRoles && requiredUserRoles.length > 0) {
      return user && user.roles
        ? requireAllUserRoles
          ? requiredUserRoles.every((role) => user.roles.includes(role))
          : requiredUserRoles.some((role) => user.roles.includes(role))
        : false;
    }

    // Check community roles first - if user has required community roles, grant access
    if (requiredCommunityRoles && requiredCommunityRoles.length > 0) {
      if (community && (community.roles)) {
        // Board members are treated as community managers, mirroring the backend
        // (CommunityRoleMiddleware). Without this the API would serve them but
        // the UI locked them out of Meetings, Requests, Residents and Units.
        const effectiveRoles = (community.roles).includes(COMMUNITY_ROLES.BOARD_MEMBER)
          ? [...(community.roles), COMMUNITY_ROLES.COMMUNITY_MANAGER]
          : (community.roles);

        const hasCommunityAccess = requireAllCommunityRoles
          ? requiredCommunityRoles.every((role) => effectiveRoles.includes(role))
          : requiredCommunityRoles.some((role) => effectiveRoles.includes(role));

        if (hasCommunityAccess) {
          return true;
        }
      }
    }

    // If no community access, check account roles
    if (requiredAccountRoles && requiredAccountRoles.length > 0) {
      if (account && account.roles) {
        return requireAllAccountRoles
          ? requiredAccountRoles.every((role) => account.roles.includes(role))
          : requiredAccountRoles.some((role) => account.roles.includes(role));
      }
      // If account roles don't exist, fall back to community roles
      if (community && (community.roles)) {
        return requireAllAccountRoles
          ? requiredAccountRoles.every((role) => (community.roles).includes(role))
          : requiredAccountRoles.some((role) => (community.roles).includes(role));
      }
      return false;
    }

    return true;
  };

  const checkPermissions = async (): Promise<void> => {
    if (isLoading || error || !user) {
      setIsChecking(false);
      if (!user) {
        logger.debug('[AuthGuard]: User is not logged in, redirecting to sign in');
        navigate(paths.auth.signIn, { replace: true });
      }
      return;
    }

    const access = getHasAccess();
    if (!access && !displayUnauthorized && !show404) {
      logger.debug('[AuthGuard]: User does not have the necessary permissions, redirecting');
      if (redirect) {
        navigate(paths.notAuthorized, { replace: true });
      }
      // Always leave the checking state: returning early here left isChecking
      // true forever, so a denied user got a permanently BLANK page instead of
      // any message (the default on Documents, Calendar and Announcements).
      setIsChecking(false);
      return;
    }

    setIsChecking(false);
  };

  React.useEffect(() => {
    checkPermissions().catch(() => {
      // noop
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps -- Expected
  }, [user, error, isLoading]);

  const hasAccess = getHasAccess();

  if (isChecking) {
    return null;
  }

  // Deny renders the unauthorized card by DEFAULT. Previously this required an
  // explicit displayUnauthorized, and the deny path never cleared isChecking, so
  // a denied user sat on a permanently blank page. Simply clearing isChecking
  // would have been worse — the component ends in `return <>{children}</>`, so
  // the page content would render for someone without access.
  if (!hasAccess && !show404) {
    return (
      <Box component="main" sx={{ flexGrow: 1, bgcolor: 'background.paper', minHeight: '100vh' }}>
        <Container maxWidth="xl" sx={{ py: 6, px: { xs: 2, md: 4 } }}>
          <Box
            sx={{
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'center',
              justifyContent: 'center',
              minHeight: '60vh',
              textAlign: 'center',
            }}
          >
            <Box
              sx={{
                width: 80,
                height: 80,
                borderRadius: '50%',
                bgcolor: 'error.lighter',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                mb: 3,
              }}
            >
              <LockOpenIcon size={40} color="var(--mui-palette-error-main)" weight="fill" />
            </Box>
            <Typography variant="h4" sx={{ fontWeight: 700, mb: 1 }}>
              Not Authorized
            </Typography>
            <Typography color="text.secondary" sx={{ mb: 4, maxWidth: 400 }} variant="body1">
              You don't have permission to access this page. If you believe you should have access, please contact your administrator.
            </Typography>
            <Stack direction="row" spacing={2}>
              <Button onClick={() => navigate(-1)} startIcon={<ArrowLeftIcon size={16} />} variant="outlined">
                Go Back
              </Button>
              <Button href={paths.home} variant="contained">
                Home
              </Button>
            </Stack>
          </Box>
        </Container>
      </Box>
    );
  }

  if (!hasAccess && show404) {
    return <NotFoundPage />;
  }

  if (error) {
    return (
      <Box component="main" sx={{ flexGrow: 1, bgcolor: 'background.paper', minHeight: '100vh' }}>
        <Container maxWidth="xl" sx={{ py: 6, px: { xs: 2, md: 4 } }}>
          <Box
            sx={{
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'center',
              justifyContent: 'center',
              minHeight: '60vh',
              textAlign: 'center',
            }}
          >
            <Typography color="error" variant="body1">
              {error}
            </Typography>
          </Box>
        </Container>
      </Box>
    );
  }

  return <>{children}</>;
}
